// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./components/LoginPage";
import Layout from "./components/Layout";
import Drivers from "./components/Drivers";
import Vehicles from "./components/Vehicles";
import Reports from "./components/Reports";
import DriverDashboard from "./components/DriverDashboard";
import DailyVehicleCheck from "./components/DailyVehicleCheck";
import VehicleCheckReviews from "./components/VehicleCheckReviews";
import "./index.css";

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const AccessDenied = ({ logout }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Account Deactivated</h2>
      <p className="text-gray-500 text-sm mb-6">Your account no longer has access to this system. Please contact your administrator.</p>
      <button
        onClick={logout}
        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
      >
        Sign Out
      </button>
    </div>
  </div>
);

// Admin only
function AdminRoute({ children }) {
  const { currentUser, userRole, logout } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (userRole === null) return <LoadingSpinner />;
  if (userRole === 'unauthorized') return <AccessDenied logout={logout} />;
  if (userRole !== 'admin') return <Navigate to="/login" />;
  return children;
}

// Driver only
function DriverRoute({ children }) {
  const { currentUser, userRole, logout } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (userRole === null) return <LoadingSpinner />;
  if (userRole === 'unauthorized') return <AccessDenied logout={logout} />;
  if (userRole !== 'driver') return <Navigate to="/login" />;
  return children;
}

// Public Route component (redirects based on role if logged in)
function PublicRoute({ children }) {
  const { currentUser, userRole } = useAuth();

  if (!currentUser) {
    return children;
  }

  // Wait for role to be loaded
  if (userRole === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect drivers to /driver-dashboard, admins to /dashboard
  if (userRole === 'driver') {
    return <Navigate to="/driver-dashboard" />;
  }

  return <Navigate to="/dashboard" />;
}

// Create a separate component for the main app logic
function AppContent() {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Driver only routes */}
        <Route
          path="/driver-dashboard"
          element={
            <DriverRoute>
              <DriverDashboard />
            </DriverRoute>
          }
        />
        <Route
          path="/daily-check"
          element={
            <DriverRoute>
              <DailyVehicleCheck />
            </DriverRoute>
          }
        />

        {/* Admin only routes */}
        <Route
          path="/"
          element={
            <AdminRoute>
              <Layout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Vehicles type="dashboard" />} />
          <Route path="drivers" element={<Drivers />} />
          <Route path="vehicles" element={<Vehicles type="list" />} />
          <Route path="reports" element={<Reports />} />
          <Route path="vehicle-checks" element={<VehicleCheckReviews />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

// Main App component with AuthProvider wrapper
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
